/**
 * growApi — Grow (Meshulam) payment links (grow_integration.md §3–§4).
 *
 * Reads go straight to Firestore (rules: finance.collectViaGrow); every write
 * goes through a Cloud Function callable — the client never writes
 * growPaymentLinks / secure docs / growIntegration settings.
 *
 * Cross-API invalidation: creating / cancelling a link stamps or clears
 * `growLinkId` on financial_transactions, and a paid callback flips statuses
 * server-side → we invalidate the same finance tags calendarApi uses
 * (CROSS_INVALIDATE_FINANCE) plus the patient-finance tags.
 *
 * Error codes: CK-7720..CK-7749 (reserved block, DEVELOPER_GUIDE §17.11).
 */

import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { httpsCallable } from "firebase/functions";
import {
  functions,
  getGrowLink,
  getGrowLinksForPatient,
  getGrowLinksForTransactions,
  getActiveGrowLinks,
} from "../../firebase";

/** Same list as calendarApi.CROSS_INVALIDATE_FINANCE — keep in sync. */
export const GROW_CROSS_INVALIDATE_FINANCE = [
  { type: "TransactionStats", id: "MONTHLY" },
  { type: "Transactions", id: "EXPECTED" },
  { type: "Transactions", id: "RANGE" },
  { type: "Transactions", id: "PAID_HISTORY" },
  { type: "KanbanTransactions", id: "LIST" },
  { type: "PendingCollection", id: "LIST" },
  { type: "AppointmentsWithoutPayments", id: "ALL" },
];

/** Patient-scoped finance tags (financeApi uses `PATIENT_${patientId}` ids). */
export const growPatientFinanceTags = (patientIds = []) =>
  [...new Set(patientIds.filter(Boolean))].flatMap((pid) => [
    { type: "Transactions", id: `PATIENT_${pid}` },
    { type: "PendingCollection", id: `PATIENT_${pid}` },
    { type: "AppointmentsWithoutPayments", id: pid },
  ]);

export const GROW_CROSS_INVALIDATE_CALENDAR = [
  { type: "Appointments", id: "LIST" },
  { type: "Appointments", id: "PENDING_ACTION" },
];

/** Dispatch finance + calendar invalidation (used by mutations and the realtime hook). */
export const invalidateFinanceAfterGrow = (dispatch, patientIds = []) => {
  dispatch({
    type: "financeApi/invalidateTags",
    payload: [...GROW_CROSS_INVALIDATE_FINANCE, ...growPatientFinanceTags(patientIds)],
  });
  dispatch({ type: "calendarApi/invalidateTags", payload: GROW_CROSS_INVALIDATE_CALENDAR });
};

/**
 * Normalize an HttpsError (or anything) into the Hebrew message the server
 * attached, falling back to a generic string. Never leaks CK codes.
 */
const toUserError = (error, fallback = "אירעה שגיאה. נסו שוב בעוד מספר דקות.") => {
  const code = error?.code || "";
  const details = error?.details || {};
  const msg = typeof error?.message === "string" ? error.message : "";
  if (msg === "TRIAL_EXPIRED") return { message: "תקופת הניסיון הסתיימה. יש לשדרג את המסלול כדי להמשיך.", code, details };
  // HttpsError messages from our functions are already Hebrew; SDK-generated ones are English.
  const looksHebrew = /[֐-׿]/.test(msg);
  return { message: looksHebrew ? msg : fallback, code, details };
};

const logErr = (ckCode, label, ctx) =>
  console.error(`[${ckCode}] ${label}`, { errorCode: ckCode, timestamp: new Date().toISOString(), ...ctx });

const call = (name) => httpsCallable(functions, name);

export const growApi = createApi({
  reducerPath: "growApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["GrowLink"],
  endpoints: (builder) => ({
    // ------------------------------------------------------------ queries
    getGrowLink: builder.query({
      async queryFn({ clinicId, linkId }) {
        try {
          const link = await getGrowLink(clinicId, linkId);
          return { data: link };
        } catch (error) {
          logErr("CK-7720", "growApi.getGrowLink failed", { errorMessage: error.message, clinicId, linkId });
          return { error: toUserError(error, "לא ניתן לטעון את דרישת התשלום.") };
        }
      },
      providesTags: (result, _e, { linkId }) => [{ type: "GrowLink", id: linkId }],
    }),

    getGrowLinksForPatient: builder.query({
      async queryFn({ clinicId, patientId, limitCount }) {
        try {
          const links = await getGrowLinksForPatient(clinicId, patientId, limitCount);
          return { data: links };
        } catch (error) {
          logErr("CK-7721", "growApi.getGrowLinksForPatient failed", { errorMessage: error.message, clinicId, patientId });
          return { error: toUserError(error, "לא ניתן לטעון דרישות תשלום של המטופל.") };
        }
      },
      providesTags: (result, _e, { patientId }) => [
        { type: "GrowLink", id: "LIST" },
        { type: "GrowLink", id: `PATIENT_${patientId}` },
        ...(result || []).map((l) => ({ type: "GrowLink", id: l.id })),
      ],
    }),

    getGrowLinksForTransactions: builder.query({
      async queryFn({ clinicId, transactionIds }) {
        try {
          const links = await getGrowLinksForTransactions(clinicId, transactionIds);
          return { data: links };
        } catch (error) {
          logErr("CK-7722", "growApi.getGrowLinksForTransactions failed", { errorMessage: error.message, clinicId, count: transactionIds?.length });
          return { error: toUserError(error, "לא ניתן לטעון דרישות תשלום.") };
        }
      },
      providesTags: (result) => [
        { type: "GrowLink", id: "LIST" },
        ...(result || []).map((l) => ({ type: "GrowLink", id: l.id })),
      ],
    }),

    getActiveGrowLinks: builder.query({
      async queryFn({ clinicId }) {
        try {
          const links = await getActiveGrowLinks(clinicId);
          return { data: links };
        } catch (error) {
          logErr("CK-7730", "growApi.getActiveGrowLinks failed", { errorMessage: error.message, clinicId });
          return { error: toUserError(error, "לא ניתן לטעון דרישות תשלום פעילות.") };
        }
      },
      providesTags: (result) => [
        { type: "GrowLink", id: "LIST" },
        { type: "GrowLink", id: "ACTIVE" },
        ...(result || []).map((l) => ({ type: "GrowLink", id: l.id })),
      ],
    }),

    // ------------------------------------------------------------ link mutations (callables)
    createGrowPaymentLink: builder.mutation({
      async queryFn({ clinicId, transactionIds, channelHint }) {
        try {
          const res = await call("createGrowPaymentLink")({ clinicId, transactionIds, channelHint });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7723", "growApi.createGrowPaymentLink failed", { errorMessage: error.message, code: error.code, clinicId, count: transactionIds?.length });
          return { error: toUserError(error, "יצירת דרישת התשלום נכשלה.") };
        }
      },
      invalidatesTags: [{ type: "GrowLink", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateFinanceAfterGrow(dispatch);
        } catch (_) {
          /* error already surfaced to caller */
        }
      },
    }),

    cancelGrowPaymentLink: builder.mutation({
      async queryFn({ clinicId, linkId }) {
        try {
          const res = await call("cancelGrowPaymentLink")({ clinicId, linkId });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7724", "growApi.cancelGrowPaymentLink failed", { errorMessage: error.message, code: error.code, clinicId, linkId });
          return { error: toUserError(error, "ביטול דרישת התשלום נכשל.") };
        }
      },
      invalidatesTags: (_r, _e, { linkId }) => [{ type: "GrowLink", id: "LIST" }, { type: "GrowLink", id: linkId }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateFinanceAfterGrow(dispatch);
        } catch (_) {
          /* noop */
        }
      },
    }),

    getGrowPaymentLinkStatus: builder.mutation({
      async queryFn({ clinicId, linkId }) {
        try {
          const res = await call("getGrowPaymentLinkStatus")({ clinicId, linkId });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7725", "growApi.getGrowPaymentLinkStatus failed", { errorMessage: error.message, code: error.code, clinicId, linkId });
          return { error: toUserError(error, "בדיקת סטטוס הקישור מול Grow נכשלה.") };
        }
      },
      invalidatesTags: (_r, _e, { linkId }) => [{ type: "GrowLink", id: linkId }, { type: "GrowLink", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.reconciled === "applied") invalidateFinanceAfterGrow(dispatch);
        } catch (_) {
          /* noop */
        }
      },
    }),

    // Phase 6 — wraps the `sendGrowPaymentLink` callable (WhatsApp-primary / SMS-fallback).
    sendGrowPaymentLink: builder.mutation({
      async queryFn({ clinicId, linkId, channel }) {
        try {
          const res = await call("sendGrowPaymentLink")({ clinicId, linkId, channel });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7726", "growApi.sendGrowPaymentLink failed", { errorMessage: error.message, code: error.code, clinicId, linkId });
          return { error: toUserError(error, "שליחת דרישת התשלום נכשלה.") };
        }
      },
      invalidatesTags: (_r, _e, { linkId }) => [{ type: "GrowLink", id: linkId }, { type: "GrowLink", id: "LIST" }],
    }),

    // ------------------------------------------------------------ settings mutations (admin callables)
    saveGrowCredentials: builder.mutation({
      async queryFn({ clinicId, userId, pageCode, accountPageCode, environment, source, settings }) {
        try {
          const res = await call("saveGrowCredentials")({ clinicId, userId, pageCode, accountPageCode: accountPageCode || undefined, environment, source, settings });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7727", "growApi.saveGrowCredentials failed", { errorMessage: error.message, code: error.code, clinicId });
          return { error: toUserError(error, "החיבור ל-Grow נכשל. בדקו את הפרטים ונסו שוב.") };
        }
      },
    }),

    disconnectGrow: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const res = await call("disconnectGrow")({ clinicId });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7728", "growApi.disconnectGrow failed", { errorMessage: error.message, code: error.code, clinicId });
          return { error: toUserError(error, "הניתוק מ-Grow נכשל.") };
        }
      },
      invalidatesTags: [{ type: "GrowLink", id: "LIST" }],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateFinanceAfterGrow(dispatch);
        } catch (_) {
          /* noop */
        }
      },
    }),

    syncGrowBranding: builder.mutation({
      async queryFn({ clinicId }) {
        try {
          const res = await call("syncGrowBranding")({ clinicId });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7731", "growApi.syncGrowBranding failed", { errorMessage: error.message, code: error.code, clinicId });
          return { error: toUserError(error, "סנכרון המיתוג ל-Grow נכשל.") };
        }
      },
    }),

    /** Phase 10 — admin refund of a Grow-collected transaction (full / partial). */
    refundGrowTransaction: builder.mutation({
      async queryFn({ clinicId, transactionId, refundSum, reason }) {
        try {
          const res = await call("refundGrowTransaction")({ clinicId, transactionId, refundSum: refundSum ?? null, reason: reason || null });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7732", "growApi.refundGrowTransaction failed", { errorMessage: error.message, code: error.code, clinicId, transactionId });
          return { error: toUserError(error, "הזיכוי דרך Grow נכשל.") };
        }
      },
      invalidatesTags: (_r, _e, { linkId }) => [{ type: "GrowLink", id: "LIST" }, ...(linkId ? [{ type: "GrowLink", id: linkId }] : [])],
      async onQueryStarted({ patientId }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          invalidateFinanceAfterGrow(dispatch, patientId ? [patientId] : []);
        } catch (_) {
          /* noop */
        }
      },
    }),

    updateGrowSettings: builder.mutation({
      async queryFn({ clinicId, settings }) {
        try {
          const res = await call("updateGrowSettings")({ clinicId, settings });
          return { data: res.data };
        } catch (error) {
          logErr("CK-7729", "growApi.updateGrowSettings failed", { errorMessage: error.message, code: error.code, clinicId });
          return { error: toUserError(error, "שמירת הגדרות Grow נכשלה.") };
        }
      },
    }),
  }),
});

export const {
  useGetGrowLinkQuery,
  useGetGrowLinksForPatientQuery,
  useGetGrowLinksForTransactionsQuery,
  useCreateGrowPaymentLinkMutation,
  useCancelGrowPaymentLinkMutation,
  useGetGrowPaymentLinkStatusMutation,
  useSendGrowPaymentLinkMutation,
  useSaveGrowCredentialsMutation,
  useDisconnectGrowMutation,
  useUpdateGrowSettingsMutation,
  useGetActiveGrowLinksQuery,
  useSyncGrowBrandingMutation,
  useRefundGrowTransactionMutation,
} = growApi;
